/**
 * Full size product image overlay.
 *
 * Opens the gallery image that is already in the page markup over the page itself, so nothing
 * new is downloaded. Built on the theme's own 'modal-dialog' element, which is why Escape,
 * click outside, focus trapping and scroll position restore are not reimplemented here.
 *
 * Navigation is handed to the page's existing slick instance instead of a second carousel.
 * A second slick would duplicate every slide, and its swipe handling cancels touchmove.
 * Delegating keeps one source of truth for "which image is showing", so the page gallery is
 * already on the right slide when the overlay closes.
 *
 * One experience on every device, tuned to the pointer at hand:
 *   Phone:   tap the picture to magnify it at that spot, drag to look around, tap to fit,
 *            swipe to change photo while fitted, pinch as usual.
 *   Desktop: three levels. A click magnifies the clicked spot to twice its size and holds
 *            still, so the mouse can rest anywhere without the view moving. A second click
 *            magnifies further at the spot just clicked, and from then on the view follows
 *            the mouse. A third click fits the picture again. The mouse wheel and the plus
 *            and minus keys walk the same three levels one at a time, arrow keys change
 *            photo, Escape closes.
 *   Both:    a zoom button top left does the same as a tap or click at the centre.
 * The old hover magnifier on the page gallery is gone (2026-09-17, the owner's call), so the
 * gallery image is a plain picture that opens this view.
 *
 * Every touch listener below is registered passive, touchmove included, so this code cannot
 * cancel a touch gesture. Page scroll on the gallery behind and pinch to zoom inside the overlay
 * are never taken away. The only non passive listener is the mouse wheel inside the overlay,
 * where the page behind is already scroll locked.
 */
(function () {
  'use strict';

  var SWIPE_MIN_X = 40; // horizontal travel before a drag counts as a swipe
  // Travel forgiven as a tap, both while the finger is down and once it is up. One number on
  // purpose: a thumb tap routinely drifts more than 10px, most of all straight after a swipe
  // while the hand is still moving, and a rule that forgave that mid gesture but not at touchend
  // still made tap to magnify fail intermittently. A scroll or a swipe crosses 24px within a few
  // frames, so the page gallery is still protected from opening on a scroll.
  var TAP_SLOP = 24;
  var MIN_SCALE = 1.5;
  var MAX_SCALE = 4;
  var WHEEL_STEP = 0.5; // scale change per wheel notch on a coarse pointer, which has no levels
  // The desktop levels. The first is a fixed closer look; the second is the picture's real
  // pixels, never less than three times, so the two steps are always clearly apart.
  var LEVEL1_SCALE = 2;
  var LEVEL2_MIN_SCALE = 3;
  // A touch screen fires a compatibility mousemove after a tap. It is ignored for this long, so
  // the hover pan written for a mouse cannot move the view after a finger lifts.
  var TOUCH_MOUSE_GUARD_MS = 700;
  // A trackpad flick delivers a burst of wheel events in a few milliseconds. One level per burst,
  // not one per event, so a flick moves one step the way a mouse notch does.
  var WHEEL_COOLDOWN_MS = 350;

  function finePointer() {
    return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  function init() {
    var overlay = document.getElementById('product-image-overlay');
    var rail = document.querySelector('.slider-for');
    if (!overlay || !rail || typeof overlay.open !== 'function') return;

    var stage = overlay.querySelector('.product-image-overlay__stage');
    var image = overlay.querySelector('.product-image-overlay__image');
    var zoomButton = overlay.querySelector('.product-image-overlay__zoom');
    var hint = overlay.querySelector('.product-image-overlay__hint');
    var index = 0;
    var zoomed = false;
    var scale = 1;
    // The desktop level: 0 fit, 1 magnified and held still, 2 magnified and following the mouse.
    // A phone has no levels; there this stays 0 and zoomed alone says what is showing.
    var level = 0;
    // Where the magnified view is anchored, as a percentage of the image box.
    var originX = 50;
    var originY = 50;
    var lastTouchAt = 0;
    var lastWheelAt = 0;

    if (hint) {
      hint.textContent = finePointer()
        ? 'Click to zoom in. Click again to zoom closer and look around with the mouse.'
        : 'Tap to zoom in. Drag to look around, swipe to change photo.';
    }

    /**
     * The gallery slides, in page order.
     * Slick clones are skipped and mediaid is deduped because the thumbnail rail on this theme
     * does clone its slides. The img.zoomImg exclusion is kept in case any other script still
     * injects a magnified copy; nothing in the theme does since 2026-09-17.
     * @returns {Array} Objects with src and alt.
     */
    function slides() {
      var seen = {};
      var out = [];
      Array.prototype.forEach.call(rail.querySelectorAll('.slider-banner-image'), function (slide) {
        if (slide.classList.contains('slick-cloned')) return;
        var id = slide.getAttribute('mediaid') || '';
        if (id) {
          if (seen[id]) return;
          seen[id] = true;
        }
        var img = slide.querySelector('img:not(.zoomImg)');
        if (img && img.getAttribute('src')) {
          out.push({ src: img.getAttribute('src'), alt: img.getAttribute('alt') || '' });
        }
      });
      return out;
    }

    function slickReady() {
      return !!(window.jQuery && rail.classList.contains('slick-initialized'));
    }

    function currentSlide() {
      if (slickReady()) {
        try {
          var i = window.jQuery(rail).slick('slickCurrentSlide');
          if (typeof i === 'number') return i;
        } catch (e) {
          /* fall through to the overlay's own index */
        }
      }
      return index;
    }

    function clampPercent(n) {
      return Math.max(0, Math.min(100, n));
    }

    function clampScale(s) {
      return Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));
    }

    /**
     * Keeps the image class, the cursor, the zoom button and the hint in step with the zoomed
     * state. The stylesheet reads the class as "the next click zooms out"; on a fine pointer the
     * first level still has a closer one above it, so its cursor and button keep saying zoom in.
     */
    function syncControls() {
      // True while another click or press would magnify further.
      var more = !zoomed || (finePointer() && level < 2);
      image.classList.toggle('is-zoomed', zoomed);
      image.style.cursor = zoomed && more ? 'zoom-in' : '';
      if (zoomButton) {
        zoomButton.classList.toggle('is-zoomed', zoomed);
        zoomButton.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        var label = zoomButton.querySelector('.visually-hidden');
        if (label) label.textContent = more ? 'Zoom in' : 'Zoom out';
        var icon = zoomButton.querySelector('i');
        if (icon) {
          icon.className = more
            ? 'fa-solid fa-magnifying-glass-plus fa-search-plus'
            : 'fa-solid fa-magnifying-glass-minus fa-search-minus';
        }
      }
      if (hint) hint.hidden = zoomed;
    }

    function setOrigin(xPercent, yPercent) {
      originX = clampPercent(xPercent);
      originY = clampPercent(yPercent);
      image.style.transformOrigin = originX + '% ' + originY + '%';
    }

    /** Anchors the magnified view at a point on screen. */
    function originFrom(clientX, clientY) {
      var r = image.getBoundingClientRect();
      setOrigin(
        r.width ? ((clientX - r.left) / r.width) * 100 : 50,
        r.height ? ((clientY - r.top) / r.height) * 100 : 50
      );
    }

    /** The magnification on screen this instant, which mid transition is not the one last set. */
    function shownScale() {
      var m = window.getComputedStyle(image).transform;
      var parts = m && m !== 'none' ? m.match(/matrix\(([^,]+),/) : null;
      var s = parts ? parseFloat(parts[1]) : 1;
      return s > 0 ? s : 1;
    }

    /**
     * Anchors the magnified view at a point on screen, measured against the picture's own box
     * rather than the box the current magnification has stretched it to. The transform never
     * moves that box, so a screen point names the same anchor whatever level is showing, and it
     * is exactly where the level 2 mouse follow settles for a pointer left at that point, so a
     * click into level 2 leaves nothing to drift. Uses the magnification on screen right now,
     * so a quick second click during the transition lands where it should.
     */
    function originFromBox(clientX, clientY) {
      var r = image.getBoundingClientRect();
      var s = shownScale();
      var w = r.width / s;
      var h = r.height / s;
      var left = r.left + ((s - 1) * originX * w) / 100;
      var top = r.top + ((s - 1) * originY * h) / 100;
      setOrigin(
        w ? ((clientX - left) / w) * 100 : 50,
        h ? ((clientY - top) / h) * 100 : 50
      );
    }

    function applyScale(s) {
      scale = clampScale(s);
      image.style.transform = 'scale(' + scale + ')';
      zoomed = true;
      syncControls();
    }

    /**
     * Moves the magnified view by a drag of dx, dy screen pixels, so the detail under the finger
     * follows the finger. With the transform anchored at an origin, a point of the image lands at
     * origin + (point - origin) * scale on screen, so shifting the picture by dx means moving the
     * origin by -dx / (scale - 1). The origin is clamped to the image box, which keeps the picture
     * covering the box: there is never a bare edge to drag into.
     * @param {number} dx - Horizontal travel since the last move, in pixels.
     * @param {number} dy - Vertical travel since the last move, in pixels.
     */
    function panBy(dx, dy) {
      if (!zoomed || scale <= 1) return;
      var w = image.clientWidth || 1;
      var h = image.clientHeight || 1;
      setOrigin(
        originX - (dx / ((scale - 1) * w)) * 100,
        originY - (dy / ((scale - 1) * h)) * 100
      );
    }

    /** The picture's real pixels, within sane bounds: a phone's one tap zoom level, and the
     *  floor under the closest desktop level. */
    function zoomScale() {
      var natural = image.naturalWidth || 0;
      var shown = image.clientWidth || 0;
      if (!natural || !shown) return 2;
      return clampScale(natural / shown);
    }

    /** The closest desktop level: the picture's real pixels, never less than three times. */
    function level2Scale() {
      return Math.min(MAX_SCALE, Math.max(LEVEL2_MIN_SCALE, zoomScale()));
    }

    function zoomAt(clientX, clientY) {
      originFrom(clientX, clientY);
      applyScale(zoomScale());
    }

    function unzoom() {
      zoomed = false;
      level = 0;
      scale = 1;
      originX = 50;
      originY = 50;
      image.style.transform = '';
      image.style.transformOrigin = '';
      syncControls();
    }

    function toggleZoom(clientX, clientY) {
      if (zoomed) {
        unzoom();
      } else {
        zoomAt(clientX, clientY);
      }
    }

    /**
     * Puts a fine pointer at one of its three levels, anchored at a point on screen when one is
     * given. Without one the anchor the view already has is kept, which is what the keys want.
     * @param {number} next - 0 fits, 1 magnifies and holds still, 2 magnifies further and
     *   follows the mouse from then on.
     * @param {number} [clientX] - Screen point to anchor at.
     * @param {number} [clientY] - Screen point to anchor at.
     */
    function stepTo(next, clientX, clientY) {
      next = Math.max(0, Math.min(2, next));
      if (next === level) return;
      if (next === 0) {
        unzoom();
        return;
      }
      if (typeof clientX === 'number') originFromBox(clientX, clientY);
      level = next;
      applyScale(next === 1 ? LEVEL1_SCALE : level2Scale());
    }

    /** A click or tap on the picture: the next level on a fine pointer, the one tap toggle on a
     *  phone. */
    function cycleZoom(clientX, clientY) {
      if (finePointer()) {
        stepTo(level >= 2 ? 0 : level + 1, clientX, clientY);
      } else {
        toggleZoom(clientX, clientY);
      }
    }

    function cycleZoomAtCentre() {
      if (finePointer()) {
        setOrigin(50, 50);
        stepTo(level >= 2 ? 0 : level + 1);
        return;
      }
      var r = image.getBoundingClientRect();
      toggleZoom(r.left + r.width / 2, r.top + r.height / 2);
    }

    /** Wheel and keyboard zoom. A fine pointer walks the three levels one at a time, anchored at
     *  the pointer on the way up when the wheel gives one, and left anchored on the way down so
     *  the picture shrinks in place; stepping down from the first level fits. A coarse pointer
     *  keeps its steps of WHEEL_STEP, anchored where the pointer is when a zoom starts from fit,
     *  and left anchored while stepping so the view does not jump. Stepping below the smallest
     *  zoom fits the picture again. */
    function zoomStep(direction, clientX, clientY) {
      if (finePointer()) {
        if (direction > 0) {
          stepTo(level + 1, clientX, clientY);
        } else {
          stepTo(level - 1);
        }
        return;
      }
      if (direction > 0) {
        if (!zoomed) {
          if (typeof clientX === 'number') originFrom(clientX, clientY);
          applyScale(MIN_SCALE);
        } else {
          applyScale(scale + WHEEL_STEP);
        }
      } else if (zoomed) {
        if (scale - WHEEL_STEP < MIN_SCALE) {
          unzoom();
        } else {
          applyScale(scale - WHEEL_STEP);
        }
      }
    }

    function render(i) {
      var list = slides();
      if (!list.length) return;
      index = Math.max(0, Math.min(i, list.length - 1));
      var item = list[index];
      // Same URL string the gallery already loaded, so this is served from cache.
      if (image.getAttribute('src') !== item.src) image.setAttribute('src', item.src);
      image.setAttribute('alt', item.alt);
      unzoom();
    }

    function goTo(i) {
      var list = slides();
      if (!list.length) return;
      var next = ((i % list.length) + list.length) % list.length;
      if (slickReady()) {
        // The page gallery owns the index. Its afterChange below calls render().
        window.jQuery(rail).slick('slickGoTo', next);
      } else {
        render(next);
      }
    }

    /**
     * Watches one surface for gestures. A horizontal drag becomes navigation, a drag while the
     * surface reports it is panning becomes a pan, and a pinch is remembered until every finger
     * has lifted so its second release can never be read as a swipe.
     * Passive on purpose: these handlers are incapable of calling preventDefault, so page
     * scroll and pinch to zoom are never cancelled.
     *
     * Each surface keeps its own flag, so a swipe inside the overlay cannot swallow the next
     * click on the gallery sitting behind it.
     * @param {Element} el - Element to watch.
     * @param {Object} [handlers] - onSwipe(1 or -1), onPan(dx, dy), panning() returning true
     *   while a one finger drag should pan instead of navigate.
     * @returns {Function} Reads and clears this surface's "that was not a clean tap" flag.
     */
    function watchGestures(el, handlers) {
      handlers = handlers || {};
      var startX = 0;
      var startY = 0;
      var lastX = 0;
      var lastY = 0;
      // Sticky from the moment a second finger lands until the last finger lifts.
      var pinched = false;
      // Set by a gesture that was a pinch, a swipe, a pan or a scroll, so the click the browser
      // fires afterwards does not also toggle zoom, close the overlay or open it.
      var suppressClick = false;

      function panning() {
        return !!(handlers.panning && handlers.panning());
      }

      el.addEventListener('touchstart', function (evt) {
        lastTouchAt = Date.now();
        if (evt.touches.length === 1) {
          pinched = false;
          startX = lastX = evt.touches[0].clientX;
          startY = lastY = evt.touches[0].clientY;
          suppressClick = false;
        } else {
          pinched = true;
          suppressClick = true;
        }
      }, { passive: true });

      el.addEventListener('touchmove', function (evt) {
        lastTouchAt = Date.now();
        if (evt.touches.length > 1) {
          pinched = true;
          suppressClick = true;
          return;
        }
        if (pinched) return;
        var t = evt.touches[0];
        if (!t) return;
        if (panning() && handlers.onPan) handlers.onPan(t.clientX - lastX, t.clientY - lastY);
        lastX = t.clientX;
        lastY = t.clientY;
        if (Math.abs(t.clientX - startX) > TAP_SLOP || Math.abs(t.clientY - startY) > TAP_SLOP) {
          suppressClick = true;
        }
      }, { passive: true });

      el.addEventListener('touchend', function (evt) {
        lastTouchAt = Date.now();
        if (evt.touches.length > 0) {
          // A finger is still down, so this lift ends nothing on its own.
          suppressClick = true;
          return;
        }
        var wasPinch = pinched;
        pinched = false;
        if (wasPinch) {
          suppressClick = true;
          return;
        }
        var touch = evt.changedTouches[0];
        var dx = touch ? touch.clientX - startX : 0;
        var dy = touch ? touch.clientY - startY : 0;
        var moved = Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP;
        if (panning()) {
          // While magnified a drag looks around and never changes image; only a clean tap
          // reaches the click that fits the picture again.
          if (moved) suppressClick = true;
          return;
        }
        if (Math.abs(dx) > SWIPE_MIN_X && Math.abs(dx) > Math.abs(dy) * 1.5) {
          suppressClick = true;
          if (handlers.onSwipe) handlers.onSwipe(dx < 0 ? 1 : -1);
          return;
        }
        if (moved) suppressClick = true;
      }, { passive: true });

      el.addEventListener('touchcancel', function () {
        lastTouchAt = Date.now();
        pinched = false;
        suppressClick = true;
      }, { passive: true });

      return function () {
        var was = suppressClick;
        suppressClick = false;
        return was;
      };
    }

    // Opening. Delegated to the rail so a click anywhere on a gallery slide opens it.
    var railGesture = watchGestures(rail);
    rail.addEventListener('click', function (evt) {
      if (!evt.target.closest || !evt.target.closest('.slider-banner-image')) return;
      if (railGesture()) return;
      // Never open with nothing to show. render() bails when the gallery holds no usable image,
      // and an overlay that opens empty is a dark screen a visitor has to guess their way out of.
      if (!slides().length) return;
      render(currentSlide());
      overlay.open(evt.target);
    });

    // Inside the overlay: swipe to change image, tap or click the image to magnify it there,
    // drag to look around while magnified, tap again to fit. A click on a fine pointer walks the
    // three levels instead, see cycleZoom.
    var stageGesture = watchGestures(stage, {
      onSwipe: function (direction) {
        goTo(index + direction);
      },
      onPan: panBy,
      panning: function () {
        return zoomed;
      }
    });

    image.addEventListener('click', function (evt) {
      if (stageGesture()) return;
      cycleZoom(evt.clientX, evt.clientY);
    });

    if (zoomButton) {
      zoomButton.addEventListener('click', function (evt) {
        evt.preventDefault();
        cycleZoomAtCentre();
      });
    }

    // Pan the magnified view with the mouse, the desktop way to look around. Only the closest
    // level follows the mouse; the first holds still so it can be studied. The guard ignores the
    // compatibility mousemove a touch screen fires after a tap.
    stage.addEventListener('mousemove', function (evt) {
      if (!zoomed) return;
      if (finePointer() && level < 2) return;
      if (Date.now() - lastTouchAt < TOUCH_MOUSE_GUARD_MS) return;
      originFrom(evt.clientX, evt.clientY);
    });

    // Mouse wheel zooms in and out inside the overlay. Not passive on purpose: the page behind is
    // scroll locked while the overlay is open, so the wheel has nothing else to do here and must
    // not bounce the locked page.
    stage.addEventListener('wheel', function (evt) {
      if (!overlay.hasAttribute('open')) return;
      evt.preventDefault();
      var now = Date.now();
      if (now - lastWheelAt < WHEEL_COOLDOWN_MS) return;
      lastWheelAt = now;
      zoomStep(evt.deltaY < 0 ? 1 : -1, evt.clientX, evt.clientY);
    }, { passive: false });

    // Keyboard: arrows change photo, plus and minus zoom. Escape is the theme's own listener.
    overlay.addEventListener('keydown', function (evt) {
      if (!overlay.hasAttribute('open')) return;
      if (evt.key === 'ArrowRight') {
        evt.preventDefault();
        goTo(index + 1);
      } else if (evt.key === 'ArrowLeft') {
        evt.preventDefault();
        goTo(index - 1);
      } else if (evt.key === '+' || evt.key === '=') {
        evt.preventDefault();
        zoomStep(1);
      } else if (evt.key === '-' || evt.key === '_') {
        evt.preventDefault();
        zoomStep(-1);
      }
    });

    // A click on the empty area around the image closes, the way a click outside does. Checked
    // against the suppression flag so a swipe that ends there is not read as a close.
    stage.addEventListener('click', function (evt) {
      if (evt.target !== stage) return;
      if (stageGesture()) return;
      overlay.close();
    });

    if (slickReady()) {
      window.jQuery(rail).on('afterChange', function (evt, slick, current) {
        if (overlay.hasAttribute('open')) {
          render(current);
        } else {
          index = current;
        }
      });
    }

    // layout/theme.liquid binds $('a.previous.round') and $('a.next.round') to slickPrev and
    // slickNext while the page parses, and this markup is already in the DOM by then, so the
    // overlay arrows are the page's arrows in both look and wiring. Only bind a fallback when
    // that wiring is genuinely absent, so a single click can never advance two slides.
    var nav = overlay.querySelector('.pre-next-btn');
    if (nav) {
      bindArrow(nav.querySelector('a.previous'), -1);
      bindArrow(nav.querySelector('a.next'), 1);
    }

    function bindArrow(link, direction) {
      if (!link || hasJqueryClick(link)) return;
      link.addEventListener('click', function (evt) {
        evt.preventDefault();
        goTo(index + direction);
      });
    }

    function hasJqueryClick(el) {
      try {
        var events = window.jQuery && window.jQuery._data(el, 'events');
        return !!(events && events.click);
      } catch (e) {
        return false;
      }
    }

    syncControls();
  }

  // The element has to be upgraded before open() and close() exist on it, the same wait
  // pop-up.js and age-verification-pop-up.js do.
  if (window.customElements) {
    customElements.whenDefined('modal-dialog').then(init);
  }
}());
